import { api, queryKeys, type Transaction } from '@alpha/api-client';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { useTableQuery } from '../../hooks/useTableQuery';
import { formatDateTime, formatUsd } from '../../lib/format';
import { Chip, Select } from '../../ui';
import { WALLET_TABS } from './Deposit';

const col = createColumnHelper<Transaction>();

export function Transactions() {
  const { query, setParam, setPage } = useTableQuery({ extraParams: ['wallet', 'flow'] });

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.transactions(query),
    queryFn: () => api.wallet.transactions(query),
  });

  const columns = useMemo(
    () => [
      col.accessor('created_at', { header: 'Date', cell: (i) => formatDateTime(i.getValue()) }),
      col.accessor('wallet', { header: 'Wallet' }),
      col.accessor('direction', {
        header: 'Type',
        cell: (i) => (
          <Chip tone={i.getValue() === 'credit' ? 'success' : 'danger'}>{i.getValue()}</Chip>
        ),
      }),
      col.accessor('amount_usd', { header: 'Amount', cell: (i) => formatUsd(i.getValue()) }),
      col.accessor('balance_after', { header: 'Balance', cell: (i) => formatUsd(i.getValue()) }),
      col.accessor('description', { header: 'Description', cell: (i) => i.getValue() ?? '—' }),
    ],
    [],
  );

  return (
    <>
      <PageHeader title="Transactions" tabs={WALLET_TABS} />
      <DataTable<Transaction>
        title="All Transactions"
        columns={columns}
        data={data?.data}
        meta={data?.meta}
        isLoading={isLoading}
        error={error as Error | null}
        onPageChange={setPage}
        toolbar={
          <>
            <Select
              value={(query.wallet as string) ?? ''}
              onChange={(e) => setParam('wallet', e.target.value)}
            >
              <option value="">All wallets</option>
              <option value="activation">Activation</option>
              <option value="income">Income</option>
            </Select>
            <Select
              value={(query.flow as string) ?? ''}
              onChange={(e) => setParam('flow', e.target.value)}
            >
              <option value="">All types</option>
              <option value="credit">Credits</option>
              <option value="debit">Debits</option>
            </Select>
          </>
        }
      />
    </>
  );
}
